import * as firestoreRedux from '@dreamworld/firestore-redux';
import * as app from '../app/index.js';
import * as router from '../router/index.js';
import * as auth from '../auth/index.js';
import * as feature from '../feature/index.js';
import * as user from '../user/index.js';
import * as providersRegistry from './providers.js';
import { getColumnName } from '../board/utils.js';
import { getIdWoPrefix } from '../../utils.js';
import { createSelector } from 'reselect';
import i18next from '@dw/i18next-esm';
import get from 'lodash-es/get.js';
import isEqual from 'lodash-es/isEqual.js';

/**
 * Positional `(state, accountId)`; `accountId` optional, defaulting to the account
 * currently in the URL - every call site in this module is already operating on the
 * current account.
 */

/** @returns {String} that account's `TimeTrackerAccountSettings` doc id. */
const accountSettingsDocId = (accountId) => `ttas_${getIdWoPrefix({ id: accountId, prefix: 'acc_' })}`;

/**
 * A subcollection of the account, not a root collection - so the path carries the
 * account and every read has to be spelled the same way the write was: firestore-redux
 * keys its state by the exact collection string it was handed, so `saga.js` and the
 * selector below drifting apart would silently look in two different buckets.
 */
export const accountSettingsPath = (accountId) => `accounts/${accountId}/time-tracker-account-settings`;

/** @returns {String} the current user's `TimeTrackerAccountUserSettings` doc id, for that account. */
const userSettingsDocId = (accountId, userId) =>
  `ttaus_${getIdWoPrefix({ id: accountId, prefix: 'acc_' })}_${getIdWoPrefix({ id: userId, prefix: 'usr_' })}`;

/**
 * Stable firestore-redux query ids, so consumers can tell "still loading" apart from
 * "loaded, and there is nothing" - `accountSettings(state)` returns undefined for both.
 * Used by the dialog restorer, which must not decide anything until the docs land.
 */
export const accountSettingsQueryId = (accountId) => `time-tracker-account-settings-${accountId}`;
export const userSettingsQueryId = (accountId) => `time-tracker-account-user-settings-${accountId}`;

/**
 * Scoped per (user, account): `saga.js`'s `load()` fires one query per account the user
 * can access, each filtered on both ids, and each needing its own trackable query id.
 */
export const activeTimersQueryId = (userId, accountId) => `time-tracker-active-timers-${userId}-${accountId}`;

/**
 * @returns {Boolean} `true` once the running-timer subscriptions have settled, so that
 * finding no timer is a fact rather than a not-yet.
 *
 * A missing status means the query has not been made at all, which is emphatically not
 * "settled" - and that was the whole bug: this tested only for `PENDING`, so before
 * `load()` had subscribed anything it answered `true`, the caller saw an empty timer list
 * and concluded there was nothing running. On a cold load carrying `?timer-action=stop`
 * that cleared the parameter within three seconds and the dialog never appeared.
 *
 * Scoped to the timer queries alone, since that is the only question anyone asks of it.
 * Account settings are deliberately not consulted: on Home there is no account in the url
 * to key them by, and a timer from any account still has to be found there.
 */
export const timersLoaded = (state) => {
  const userId = auth.selectors.currentUserId(state);
  const accounts = auth.selectors.accessibleAccounts(state) || [];
  if (!userId || !accounts.length) {
    return false;
  }
  return accounts.every((accId) => {
    const id = activeTimersQueryId(userId, accId);
    const status = firestoreRedux.selectors.queryStatus(state, id);
    if (!status || status === 'PENDING') {
      return false;
    }
    // `LIVE` arrives with the query's first snapshot. A `result` array alone is not the
    // test: firestore-redux never writes one for a query that answered empty, so an
    // account without a timer read as never loaded.
    return status === 'LIVE' || Array.isArray(get(firestoreRedux.selectors.query(state, id), 'result'));
  });
};

/** @returns {Object|undefined} that account's `TimeTrackerAccountSettings` (provider, status, preferences). */
export const accountSettings = (state, accountId) => {
  accountId = accountId || router.selectors.accountId(state);
  return firestoreRedux.selectors.doc(state, 'time-tracker-account-settings', accountSettingsDocId(accountId));
};

/**
 * @returns {String} `NOT_CONNECTED` (no document) | `CONNECTED` | `ACTIVATED` | `REVOKED`.
 *
 * `CONNECTED` and `ACTIVATED` are two distinct live states, not synonyms: the admin key
 * verifies first (`CONNECTED`), and the account only becomes usable once a default Client
 * is chosen (`ACTIVATED`). Everything that offers to *start* a timer wants `isActivated`;
 * `isConnected` only says a working key exists.
 */
export const status = (state, accountId) => get(accountSettings(state, accountId), 'status') || 'NOT_CONNECTED';

/** @returns {Boolean} `true` when a verified admin key exists - activated or not yet. */
export const isConnected = (state, accountId) => ['CONNECTED', 'ACTIVATED'].includes(status(state, accountId));

/**
 * @returns {Boolean} `true` only when setup is finished and timers can actually run.
 *
 * Starting against a merely `CONNECTED` account fails server-side with `NOT_ACTIVATED` -
 * there is no default Client to create the Board's project under - so the affordance is
 * withheld rather than offered and then rejected.
 */
export const isActivated = (state, accountId) => status(state, accountId) === 'ACTIVATED';

/**
 * @returns {Object} `{ defaultCustomerId, startTimerOnInProgress, descriptionRequired,
 *   stopTimerOnDoneOrTrash }`, with `stopTimerOnDoneOrTrash` `false` for an account that
 *   never saved it.
 */
export const preferences = createSelector(
  (state, accountId) => get(accountSettings(state, accountId), 'preferences'),
  (storedPreferences) => ({ stopTimerOnDoneOrTrash: false, ...storedPreferences }),
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/** @returns {Object|undefined} the connected provider's registry entry, or `undefined` when not connected. */
export const provider = (state, accountId) => {
  const settings = accountSettings(state, accountId);
  return settings && providersRegistry.get(settings.provider);
};

/** @returns {Array} the full provider registry, for the picker. Static client-side data - takes no accountId. */
export const providers = () => providersRegistry.list();

/**
 * Two videos per provider, not one: an admin obtains an *admin* API key and a member
 * obtains a *personal* one, and in Kimai those are found in different places, so a
 * single walkthrough would be wrong for one of the two audiences.
 *
 * Keyed by provider id rather than account, because the connect dialog needs this
 * before anything is stored - the provider has only just been picked.
 *
 * Lives in the app config (Firestore `app/config`, merged over the static config) so
 * Support can publish or swap either video without a release. Empty until one is
 * configured, and the dialogs skip the link entirely rather than offering a dead one.
 *
 * @param {Object} state Redux state
 * @param {String} providerId provider id, e.g. 'kimai'
 * @param {String} audience `'admin'` (account-level key) | `'user'` (personal key)
 * @returns {String} Vimeo url of the "how to obtain an API key" video, or ''.
 */
export const helpVideoUrl = (state, providerId, audience = 'admin') => {
  if (!providerId) {
    return '';
  }
  return get(app.selectors.config(state), `timeTracker.helpVideoUrl.${providerId}.${audience}`) || '';
};

/** @returns {Object|undefined} current user's `TimeTrackerAccountUserSettings`, for that account. */
export const userSettings = (state, accountId) => {
  accountId = accountId || router.selectors.accountId(state);
  const userId = auth.selectors.currentUserId(state);
  if (!userId) {
    return undefined;
  }
  return firestoreRedux.selectors.doc(state, `time-tracker-account-user-settings`, userSettingsDocId(accountId, userId));
};

/** @returns {Boolean} `true` when this user has a working personal key in that account. */
/**
 * Keyed on `externalUserId`, because `TimeTrackerAccountUserSettings` carries no status
 * field at all - the entity model is explicit that `externalUserId` "is what tells the UI
 * this user is connected", since the `apiToken` beside it is encrypted and cannot serve
 * that purpose. Testing a `status` that never exists read as *nobody is ever connected*,
 * so a member with a perfectly good personal key was asked for it on every start.
 *
 * There is no stored "revoked" for a personal key either: a token that stops working is
 * only discovered when it is used, and the server says so with `USER_TOKEN_INVALID`.
 */
export const isUserConnected = (state, accountId) => !!get(userSettings(state, accountId), 'externalUserId');

/**
 * The current user's running timer.
 *
 * With an account in play - a board, Account Settings - that account's timer, since
 * `ActiveTimer` is 1:1 per (account, user). Without one, the most recently started of
 * *any* account's: Home/Explore spans every account the user can reach, and a timer they
 * started elsewhere still has to surface in the header there.
 *
 * @param {Object} state Redux state
 * @param {String} [accountId] defaults to the account in the URL, if there is one
 * @returns {Object|undefined} the running `ActiveTimer`
 */
export const active = (state, accountId) => {
  const userId = auth.selectors.currentUserId(state);
  if (!userId) {
    return undefined;
  }

  accountId = accountId || router.selectors.accountId(state);

  // Filtered out of the collection rather than read by id: an `ActiveTimer` lives under
  // the card being timed and carries a random `ttat_` suffix, so there is no id to
  // compute from (account, user) any more. `startedAt` breaks ties - the id no longer
  // guarantees there is only one, see `activeList`.
  return Object.values(firestoreRedux.selectors.collection(state, 'active-timers') || {})
    .filter((doc) => doc && doc.userId === userId && (!accountId || doc.accountId === accountId))
    .sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0))[0];
};

/**
 * Every timer the current user has running, newest first.
 *
 * Expected to be at most one per account, but nothing in the schema enforces that any
 * more - the old `at_$accountId_$userId` id made a second timer impossible to write,
 * whereas a random `ttat_` id leaves it to the server. Sorted so that if a duplicate
 * ever does land, the newest wins consistently here and in `active`.
 *
 * Meaningful on Home/Explore, which spans every account the user can reach; anywhere
 * with an account in play, `active` is the one to use.
 *
 * @param {Object} state Redux state
 * @returns {Array} running `ActiveTimer` documents
 */
export const activeList = (state) => {
  const userId = auth.selectors.currentUserId(state);
  if (!userId) {
    return [];
  }

  const all = firestoreRedux.selectors.collection(state, 'active-timers') || {};
  return Object.values(all)
    .filter((doc) => doc && doc.userId === userId)
    .sort((a, b) => (b.startedAt || 0) - (a.startedAt || 0));
};

/**
 * The one timer a page should surface: the url account's own if that account has one,
 * and otherwise the newest running anywhere.
 *
 * Not `active` itself, which answers strictly for the account it is asked about and has
 * to keep doing so - the sagas ask it whether *this* account is busy before starting or
 * stopping, and a board card asks it whether *its* card is the one running. Only the
 * header pill and the stop FAB want the fallback, because they speak for the person
 * rather than for the page: a timer left running on another account is still running,
 * and hiding it on every board but its own is how it gets forgotten.
 *
 * @param {Object} state Redux state
 * @param {String} [accountId] defaults to the account in the URL, if there is one
 * @returns {Object|undefined} the running `ActiveTimer`
 */
export const visible = (state, accountId) => {
  accountId = accountId || router.selectors.accountId(state);
  return (accountId && active(state, accountId)) || activeList(state)[0];
};

/**
 * @returns {Object|undefined} `{ status, billable, editable }` for the running entry.
 *
 * Absent until `loadBillable` has answered, and deliberately not cached between opens -
 * the tracker resolves `billable` through its own Client/Project/Activity inheritance and
 * can withdraw the right to override it at any time, so a remembered answer would leave
 * the toggle live for someone who may no longer change it.
 */
export const billable = (state, accountId) => {
  accountId = accountId || router.selectors.accountId(state);
  return get(state, `time-tracker.billable.${accountId}`);
};

/**
 * @returns {Object|undefined} `{ status, entry }` for this person's last closed entry.
 *
 * `entry` is undefined on a `SUCCESS` whose answer was 204 - nothing has ever been closed
 * on the tracker, which is a normal first-use state and not an error.
 */
export const lastEntry = (state, accountId) => {
  accountId = accountId || router.selectors.accountId(state);
  return get(state, `time-tracker.lastEntry.${accountId}`);
};

/** @returns {Object|undefined} `{ status, list }` - provider tags cached for that account. */
export const tags = (state, accountId) => {
  accountId = accountId || router.selectors.accountId(state);
  return get(state, `time-tracker.tags.${accountId}`);
};

/** @returns {Object|undefined} `{ status, list }` - provider customers cached for that account. */
export const customers = (state, accountId) => {
  accountId = accountId || router.selectors.accountId(state);
  return get(state, `time-tracker.customers.${accountId}`);
};

const NO_DISMISSED_MOVES = {};

/**
 * @returns {Object} the move prompts closed on this device, as `{ [timerId]: lastMovedAt }`.
 * Takes no accountId.
 */
export const dismissedMoves = (state) => get(state, 'time-tracker.dismissedMoves') || NO_DISMISSED_MOVES;

const DONE_OR_TRASH_COLUMN_TYPES = ['DONE', 'TRASH'];
const START_ROUNDING_GRACE_MS = 60000;

/**
 * The timed card's latest move into Done or Trash, while it is still unanswered on this
 * device. Nothing is loaded for it: the column and the mover are named only from what the
 * store already holds.
 *
 * @param {Object} state Redux state
 * @param {String} cardId the card a running timer of the current user is on
 * @returns {Object|null} `{ timerId, accountId, cardId, columnType, columnName, movedAt,
 *   isMovedBySomeoneElse, moverName }`, or `null` when no prompt is due for that card
 */
export const doneOrTrashMove = createSelector(
  (state) => state,
  (state, cardId) => cardId,
  (state, cardId) => {
    const timer = cardId && activeList(state).find((doc) => doc.cardId === cardId);
    if (!timer || !preferences(state, timer.accountId).stopTimerOnDoneOrTrash) {
      return null;
    }

    const card = firestoreRedux.selectors.doc(state, 'cards', cardId);
    if (!card || !DONE_OR_TRASH_COLUMN_TYPES.includes(card.columnType)) {
      return null;
    }

    // A timer started on a card already in Done or Trash: the card did not move. The grace
    // is because the tracker rounds `startedAt` up to the minute.
    const movedAt = card.lastMovedAt;
    if (!(movedAt > timer.startedAt - START_ROUNDING_GRACE_MS)) {
      return null;
    }
    if (dismissedMoves(state)[timer.id] === movedAt) {
      return null;
    }

    const column = card.columnId && firestoreRedux.selectors.doc(state, 'columns', card.columnId);
    const isMovedBySomeoneElse = !card.lastMovedBy || card.lastMovedBy !== auth.selectors.currentUserId(state);
    const mover = isMovedBySomeoneElse && card.lastMovedBy && user.selectors.attrs(state, card.lastMovedBy);
    const moverName = mover ? `${mover.firstName || ''} ${mover.lastName || ''}`.trim() : '';

    return {
      timerId: timer.id,
      accountId: timer.accountId,
      cardId,
      columnType: card.columnType,
      columnName: (column && column.type === card.columnType && getColumnName(column))
        || i18next.t(`defaultColumns.${card.columnType}`),
      movedAt,
      isMovedBySomeoneElse,
      moverName,
    };
  },
  {
    memoizeOptions: {
      resultEqualityCheck: isEqual,
      maxSize: 50,
    },
  }
);

/** @returns {Object|null} the card whose Start Timer is waiting on a blocking dialog. Takes no accountId. */
export const pendingStart = (state) => get(state, 'time-tracker.pendingStart') || null;

/** @returns {String|undefined} `IN_PROGRESS` | `SUCCESS` | `FAILED`, for a named operation. */
export const requestStatus = (state, name) => get(state, `time-tracker.requests.${name}.status`);

/** @returns {Object|undefined} `{ code, message }`, present only when that operation's status is `FAILED`. */
export const requestError = (state, name) => get(state, `time-tracker.requests.${name}.error`);

/** @returns {Boolean} feature-flag gate; wraps `feature.selectors.isAvailable`. */
export const isAvailable = (state, accountId) => feature.selectors.isAvailable(state, 'time-tracker', accountId);
